/**
 * RegistroGastosAgregar.js — Formulario para registrar un nuevo gasto
 * Genera automáticamente un asiento de diario:
 *   DEBE  → Cuenta de Gasto (GASTO)
 *   HABER → Cuenta de Banco (ACTIVO)
 */
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import apiFetch from "../../../../shared/services/apiClient";
import { formatCurrency } from "../../../../shared/utils/formatCurrency";
import invoiceStyles from "../../invoices/styles/invoice.module.css";
import addStyles     from "../../invoices/styles/addInvoice.module.css";

function FieldError({ msg }) {
  if (!msg) return null;
  return (
    <div style={{ display:"flex", alignItems:"center", gap:5,
      color:"#f87171", fontSize:"0.77rem", marginTop:4, fontWeight:500 }}>
      <i className="fa-solid fa-circle-exclamation" style={{ fontSize:"0.72rem" }}/>
      {msg}
    </div>
  );
}

function FormGasto({ onCancel, onSaved }) {
  const today = new Date().toISOString().split("T")[0];

  const [form, setForm] = useState({
    fecha:        today,
    referencia:   "",
    detalle:      "",
    monto:        "",
    cuentaBanco:  null,
    cuentaBancoNombre: "",
    cuentaIngreso: null,
    cuentaIngresoNombre: "",
  });

  const [cuentasBanco,   setCuentasBanco]   = useState([]);
  const [cuentasIngreso, setCuentasIngreso] = useState([]);
  const [errors,  setErrors]  = useState({});
  const [saving,  setSaving]  = useState(false);
  const [apiErr,  setApiErr]  = useState("");

  useEffect(() => {
    // Usar endpoint de cuentas bancarias reales (no todo el catálogo ACTIVO)
    apiFetch("/api/finance/cuentaBancaria/")
      .then(r => r.json())
      .then(d => {
        const list = Array.isArray(d) ? d : (d.results ?? []);
        // Mapear al formato {id, codigo, nombre} usando cuenta_contable como id
        setCuentasBanco(list.map(b => ({
          id:     b.cuenta_contable,          // FK al catálogo (lo que necesita el asiento)
          codigo: `${b.banco}`,
          nombre: `${b.banco} — ${b.numero_cuenta}`,
          saldo:  b.saldo,
        })));
      });

    apiFetch("/api/finance/catalogo/detalle/?tipo=GASTO")
      .then(r => r.json())
      .then(d => {
        const list = Array.isArray(d) ? d : (d.results ?? []);
        setCuentasIngreso(list);
        if (list.length > 0) {
          setForm(p => ({ ...p, cuentaIngreso: list[0].id,
            cuentaIngresoNombre: list[0].nombre }));
        }
      });
  }, []);

  function set(f, v) { setForm(p => ({ ...p, [f]: v })); }

  function validate() {
    const e = {};
    if (!form.fecha)                          e.fecha        = "La fecha es requerida";
    if (!form.detalle.trim())                 e.detalle      = "El detalle es requerido";
    if (!form.monto || Number(form.monto)<=0) e.monto        = "El monto debe ser mayor a 0";
    if (!form.cuentaBanco)                    e.cuentaBanco  = "Seleccioná la cuenta de banco";
    if (!form.cuentaIngreso)                  e.cuentaIngreso= "Seleccioná la cuenta de gasto";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSubmit(ev) {
    ev.preventDefault();
    setApiErr("");
    if (!validate()) return;
    setSaving(true);
    try {
      const monto = Number(form.monto);
      const concepto = form.detalle.trim() +
        (form.referencia.trim() ? ` | Ref: ${form.referencia.trim()}` : "");

      const res = await apiFetch("/api/finance/asientos/", {
        method: "POST",
        body: JSON.stringify({
          fecha:   form.fecha,
          concepto,
          tipo:    "GASTO",
          lineas: [
            // DEBE → Gasto (GASTO: debitar = aumenta gasto)
            { cuenta: form.cuentaIngreso, debito: monto,  credito: 0,
              descripcion: `Gasto — ${form.cuentaIngresoNombre}` },
            // HABER → Banco (ACTIVO: creditar = sale dinero)
            { cuenta: form.cuentaBanco,  debito: 0, credito: monto,
              descripcion: `Banco — ${form.cuentaBancoNombre}` },
          ],
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const k = Object.keys(err)[0];
        throw new Error(k ? `${k}: ${Array.isArray(err[k]) ? err[k][0] : err[k]}` : "Error al guardar");
      }
      const created = await res.json();
      onSaved(created);
    } catch (ex) { setApiErr(ex.message); }
    finally { setSaving(false); }
  }

  const montoNum = Number(form.monto) || 0;
  const errorCount = Object.keys(errors).length;

  return (
    <>
      {/* Breadcrumb */}

      <span className={invoiceStyles.inactive}>
        <Link to="/">
          <span className={invoiceStyles.inactive}>Inicio /</span>
        </Link>
        <Link to="/finanzas/gastos">
          <span className={invoiceStyles.inactive}> Registro de Gastos /</span>
        </Link>
          <span className={invoiceStyles.active}> Agregar</span>
      </span>

      <h2 style={{ margin:"10px 0 20px", fontSize:20, fontWeight:600, color:"#e5ecf5",
        display:"flex", alignItems:"center", gap:8 }}>
        <span style={{ background:"rgba(251, 146, 60, 0.15)", color:"#fb923c",
          padding:"6px 8px", borderRadius:8 }}>
          <i className="fa-solid fa-arrow-trend-down" style={{color:"#fb923c"}}/>
        </span>
        Registrar Gasto
      </h2>

      {/* Errores */}
      {errorCount > 0 && (
        <div style={{ background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.35)",
          borderLeft:"4px solid #ef4444", borderRadius:8, padding:"12px 18px", marginBottom:16 }}>
          <div style={{ display:"flex", alignItems:"center", gap:8,
            color:"#f87171", fontWeight:600, marginBottom:6 }}>
            <i className="fa-solid fa-triangle-exclamation"/>
            Hay {errorCount} campo{errorCount>1?"s":""} con error
          </div>
          <ul style={{ margin:0, paddingLeft:20, color:"#fca5a5", fontSize:"0.83rem" }}>
            {Object.values(errors).map((m,i) => <li key={i}>{m}</li>)}
          </ul>
        </div>
      )}
      {apiErr && (
        <div style={{ background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.35)",
          borderLeft:"4px solid #ef4444", borderRadius:8, padding:"12px 18px",
          marginBottom:16, color:"#f87171", fontSize:"0.88rem",
          display:"flex", alignItems:"center", gap:8 }}>
          <i className="fa-solid fa-circle-exclamation"/>{apiErr}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className={addStyles["invoice-container"]}>

          {/* ── Izquierda: campos ── */}
          <div className={addStyles["invoice-details"]}>
            <div className={addStyles["invoice-details-header"]}>
              <h1>
                <i className={`fa-solid fa-arrow-trend-down ${addStyles["icon-invoice-details"]}`}/>
                Detalles del Gasto
              </h1>
            </div>

            <div className={addStyles["invoice-details-body"]} style={{ marginTop:14 }}>

              {/* Fecha + Referencia */}
              <div className="row">
                <div className="col">
                  <label>Fecha <span style={{ color:"#f87171" }}>*</span></label>
                  <input type="date" className="form-control"
                    value={form.fecha}
                    onChange={e => set("fecha", e.target.value)}
                    style={{ colorScheme:"dark",
                      borderColor:errors.fecha ? "#ef4444" : undefined }}/>
                  <FieldError msg={errors.fecha}/>
                </div>
                <div className="col">
                  <label>Referencia</label>
                  <input type="text" className="form-control"
                    placeholder="N° cheque, transferencia, etc."
                    value={form.referencia}
                    onChange={e => set("referencia", e.target.value)}/>
                </div>
              </div>

              {/* Detalle */}
              <div>
                <label>Detalle <span style={{ color:"#f87171" }}>*</span></label>
                <input type="text" className="form-control"
                  placeholder="Descripción del ingreso…"
                  value={form.detalle}
                  onChange={e => set("detalle", e.target.value)}
                  style={{ borderColor:errors.detalle ? "#ef4444" : undefined }}/>
                <FieldError msg={errors.detalle}/>
              </div>

              {/* Monto */}
              <div>
                <label>Monto <span style={{ color:"#f87171" }}>*</span></label>
                <input type="number" min="0.01" step="0.01" className="form-control"
                  placeholder="0.00" value={form.monto}
                  onChange={e => set("monto", e.target.value)}
                  style={{ borderColor:errors.monto ? "#ef4444" : undefined }}/>
                <FieldError msg={errors.monto}/>
              </div>

              {/* Cuenta Gasto (DEBE) */}
              <div>
                <label>
                  Cuenta de Banco <span style={{ color:"#f87171" }}>*</span>
                  <span style={{ color:"#f87171", fontWeight:400,
                    marginLeft:6, fontSize:"0.74rem" }}>Activo</span>
                </label>
                <select className="form-select"
                  value={form.cuentaBanco ?? ""}
                  onChange={e => {
                    const id = Number(e.target.value);
                    const obj = cuentasBanco.find(c => c.id === id);
                    set("cuentaBanco", id || null);
                    set("cuentaBancoNombre", obj?.nombre ?? "");
                  }}
                  style={{ borderColor:errors.cuentaBanco ? "#ef4444" : undefined }}>
                  <option value="">Seleccioná cuenta de banco…</option>
                  {cuentasBanco.map(c => (
                    <option key={c.id} value={c.id}>{c.codigo} — {c.nombre}</option>
                  ))}
                </select>
                <FieldError msg={errors.cuentaBanco}/>
              </div>

              {/* Cuenta Gasto (DEBE) */}
              <div>
                <label>
                  Cuenta de Gasto <span style={{ color:"#f87171" }}>*</span>
                  <span style={{ color:"#60a5fa", fontWeight:400,
                    marginLeft:6, fontSize:"0.74rem" }}>Gasto</span>
                </label>
                <select className="form-select"
                  value={form.cuentaIngreso ?? ""}
                  onChange={e => {
                    const id = Number(e.target.value);
                    const obj = cuentasIngreso.find(c => c.id === id);
                    set("cuentaIngreso", id || null);
                    set("cuentaIngresoNombre", obj?.nombre ?? "");
                  }}
                  style={{ borderColor:errors.cuentaIngreso ? "#ef4444" : undefined }}>
                  <option value="">Seleccioná cuenta de gasto…</option>
                  {cuentasIngreso.map(c => (
                    <option key={c.id} value={c.id}>{c.codigo} — {c.nombre}</option>
                  ))}
                </select>
                <FieldError msg={errors.cuentaIngreso}/>
              </div>

            </div>
          </div>

          {/* ── Derecha: resumen ── */}
          <div className={addStyles["filial-details"]}>
            <div className={addStyles["filial-details-header"]}>
              <h1>
                <i className={`fa-solid fa-receipt ${addStyles["icon-filial-details"]}`}/>
                Resumen
              </h1>
            </div>

            <div className={addStyles["invoice-details-body"]} style={{ marginTop:14 }}>
              <div className={addStyles["filial-details-summary"]}>
                <div className={addStyles["filial-summary-relevant"]}>
                  <h2>RESUMEN RÁPIDO</h2>
                  {[
                    ["Fecha",      form.fecha || "—"],
                    ["Referencia", form.referencia || "—"],
                    ["Detalle",    form.detalle || "—"],
                    ["Banco",      form.cuentaBancoNombre || "—"],
                    ["Gasto",      form.cuentaIngresoNombre || "—"],
                  ].map(([label, val]) => (
                    <div key={label} style={{ display:"flex", justifyContent:"space-between",
                      marginBottom:4, fontSize:14 }}>
                      <span style={{ color:"#6b7280" }}>{label}</span>
                      <span style={{ color:"rgba(141,152,167,0.7)", maxWidth:130,
                        textAlign:"right", overflow:"hidden", textOverflow:"ellipsis",
                        whiteSpace:"nowrap" }}>{val}</span>
                    </div>
                  ))}
                </div>
                <div className={addStyles["filial-summary-important"]} style={{ marginTop:12 }}>
                  <h2>Total Gasto</h2>
                  <span>₡{Intl.NumberFormat("es-ES",{minimumFractionDigits:2}).format(montoNum)}</span>
                </div>
              </div>

              {/* Preview asiento */}
              <div style={{ padding:"10px 12px", background:"rgba(55,65,81,0.1)",
                borderRadius:6, border:"1px solid #364250", marginTop:8,
                fontFamily:"monospace", fontSize:12, color:"#8d98a7", lineHeight:1.9 }}>
                <span style={{ color:"#f87171" }}>DEBE</span>{"  "}
                {form.cuentaBancoNombre   || "Banco (ACTIVO)"}<br/>
                <span style={{ color:"#60a5fa" }}>HABER</span>{"  "}
                {form.cuentaIngresoNombre || "Gasto (GASTO)"}
              </div>
            </div>
          </div>

          {/* ── Footer ── */}
          <div className={addStyles["invoice-footer"]}>
            <div className={addStyles.actions}>
              <button type="button" onClick={onCancel}
                className={`${addStyles.btn} ${addStyles["btn-clean-form"]}`}>
                <i className="fa-solid fa-arrow-left" style={{ marginRight:6 }}/>
                Cancelar
              </button>
              <button type="submit" disabled={saving}
                className={`${addStyles.btn} ${addStyles["btn-save"]}`}
                style={{ opacity:saving ? 0.7 : 1 }}>
                {saving
                  ? <><i className="fa-solid fa-circle-notch fa-spin"/> Guardando…</>
                  : <><i className="fa-solid fa-floppy-disk"/> Guardar Gasto</>}
              </button>
            </div>
          </div>

        </div>
      </form>
    </>
  );
}

// ── Pantalla éxito ────────────────────────────────────────────────────────────
function Exito({ asiento, onNuevo, onVolver }) {
  return (
    <div style={{ maxWidth:560, margin:"24px 0" }}>
      <div style={{ background:"#1f2937", border:"1px solid #364250",
        borderRadius:8, padding:32, textAlign:"center" }}>
        <div style={{ fontSize:"2.5rem", marginBottom:12 }}>✅</div>
        <h2 style={{ fontSize:16, fontWeight:700, color:"#e5ecf5", margin:"0 0 4px" }}>
          {asiento.numero}
        </h2>
        <p style={{ color:"#8d98a7", fontSize:14, margin:"0 0 20px" }}>
          Gasto registrado y asiento de diario generado correctamente
        </p>
        <div style={{ background:"rgba(74,222,128,0.07)",
          border:"1px solid rgba(74,222,128,0.25)", borderRadius:6,
          padding:"12px 16px", textAlign:"left", marginBottom:20 }}>
          <div style={{ color:"#fb923c", fontWeight:600, fontSize:14, marginBottom:6 }}>
            <i className="fa-solid fa-book-open" style={{ marginRight:6 }}/>
            Asiento generado: {asiento.numero}
          </div>
          <div style={{ fontFamily:"monospace", fontSize:12, color:"#8d98a7", lineHeight:1.9 }}>
            {(asiento.lineas ?? []).map((l, i) => (
              <div key={i}>
                <span style={{ color:Number(l.debito)>0 ? "#f87171" : "#60a5fa" }}>
                  {Number(l.debito)>0 ? "DEBE " : "HABER"}
                </span>
                {"  "}{l.cuenta_nombre}{"  "}
                ₡{Intl.NumberFormat("es-ES",{minimumFractionDigits:2})
                    .format(Number(l.debito)||Number(l.credito))}
              </div>
            ))}
          </div>
        </div>
        <div style={{ display:"flex", gap:10, justifyContent:"center" }}>
          <button onClick={onNuevo}
            style={{ padding:"9px 18px", background:"transparent",
              border:"1px solid #364250", borderRadius:6, color:"#e5ecf5",
              fontWeight:600, cursor:"pointer" }}>
            Nuevo Gasto
          </button>
          <button onClick={onVolver}
            style={{ padding:"9px 18px", background:"#f97316", border:"none",
              borderRadius:6, color:"#fff", fontWeight:600, cursor:"pointer" }}>
            Ver Lista
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Página principal (controlador de vistas) ──────────────────────────────────

// ── Página principal ──────────────────────────────────────────────────────────
export default function RegistroGastosAgregar() {
  const navigate = useNavigate();
  const [vista, setVista] = useState("form");
  const [ultimoAsiento, setUltimoAsiento] = useState(null);

  if (vista === "exito")
    return (
      <Exito
        asiento={ultimoAsiento}
        onNuevo={() => setVista("form")}
        onVolver={() => navigate("/finanzas/gastos")}
      />
    );

  return (
    <FormGasto
      onCancel={() => navigate("/finanzas/gastos")}
      onSaved={a => { setUltimoAsiento(a); setVista("exito"); }}
    />
  );
}
